import React, {useCallback, useEffect, useState} from 'react';
import {AuthApiError, User} from '@ergeon/erg-auth';
import {AuthProviderProps} from './types';

import {getResponseStatus, reportError} from '../../../utils/reporting';

// Expected sign-in control flow: the login card is the product behavior for
// an unauthenticated or unauthorized visitor, so these produce no event.
const EXPECTED_AUTH_REASONS: AuthApiError['reason'][] = ['not-authenticated', 'not-authorized', 'missing-token'];

// Auth outages (network/5xx → reason 'unknown') previously produced no signal
// at all (CAD-6698 §3 #17). Report the underlying error so the original stack
// survives; never `authError` itself, which carries the response payload the
// reporter would stringify into the event (CAD-6756 §10B).
//
// `retrieveUser()` wraps `getUser()` in a Promise executor, so anything that
// throws synchronously in there (corrupted `requestUser` storage, malformed
// JWT, missing config) rejects with a raw Error carrying no `reason` and no
// `.error`. Those are exactly the real failures this exists for, so fall back
// to the caught value whenever it is already an Error and only synthesize one
// when there is genuinely neither.
const getAuthCause = (rejection: unknown): unknown => {
  const {error} = rejection as AuthApiError;
  if (error) return error;
  return rejection instanceof Error ? rejection : undefined;
};

const reportAuthOutage = (rejection: unknown) => {
  const authError = rejection as AuthApiError;
  if (EXPECTED_AUTH_REASONS.includes(authError.reason)) return;
  const cause = getAuthCause(rejection);
  reportError(cause ?? new Error(`Auth user retrieve failed (${authError.reason})`), {
    operation: 'auth_user_retrieve',
    handled: true,
    extra: {
      reason: authError.reason,
      status_code: getResponseStatus(cause) ?? getResponseStatus(authError),
    },
  });
};

/**
 * Allows connecting the Auth through passing a necessary options to the child component.
 * Expect from the child component to implement needed UI.
 * @param props auth service and children as a function
 * @returns ReactNode
 */
function AuthProvider(props: AuthProviderProps) {
  const {authService, children} = props;

  const [user, setUser] = useState<User>();
  const [error, setError] = useState<AuthApiError>();

  const onAuth = useCallback(() => {
    void authService.authenticate();
  }, [authService]);

  const getUser = useCallback(async () => {
    try {
      setUser(Object.freeze(await authService.retrieveUser()));
    } catch (error) {
      reportAuthOutage(error);
      setError(error as AuthApiError);
    }
  }, [authService]);

  useEffect(() => {
    // onMount
    if (!user) {
      void getUser();
    }
    authService.on('refresh', getUser);

    return () => {
      // onUnmount
      authService.off('refresh', getUser);
    };
  }, [authService, user]);

  return <>{children({user, error, onAuth})}</>;
}

export default AuthProvider;
